import { useState } from "react";
import { Copy, Check, Code2 } from "lucide-react";

const SITE_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const SNIPPETS = {
  iframe: `<!-- Melbourne Grand Mosque Donation Widget -->
<iframe
  src="${SITE_URL}/"
  title="Donate to Melbourne Grand Mosque"
  style="width:100%; max-width:560px; height:1100px; border:0; display:block; margin:0 auto; background:#E8E6F5; border-radius:16px;"
  loading="lazy"
  allow="payment *"
></iframe>`,
  responsive: `<!-- Responsive Melbourne Grand Mosque Donation Widget -->
<div style="width:100%;max-width:560px;margin:0 auto;">
  <iframe
    src="${SITE_URL}/"
    title="Donate to Melbourne Grand Mosque"
    style="width:100%;height:1100px;border:0;background:#E8E6F5;border-radius:16px;"
    loading="lazy"
    allow="payment *"
  ></iframe>
</div>`,
  shortcode: `[iframe src="${SITE_URL}/" width="100%" height="1100" frameborder="0"]`,
};

function Snippet({ label, code, testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {}
  };
  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Code2 className="w-4 h-4 text-slate-600" />
          <span className="font-semibold text-slate-900 text-sm">{label}</span>
        </div>
        <button
          onClick={copy}
          data-testid={testId}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0F172A] hover:bg-[#1E293B] text-white text-xs font-semibold rounded-lg transition"
        >
          {copied ? (
            <>
              <Check className="w-3.5 h-3.5" /> Copied
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5" /> Copy
            </>
          )}
        </button>
      </div>
      <pre className="px-4 py-3 text-xs text-slate-800 overflow-x-auto whitespace-pre-wrap break-all">
        <code>{code}</code>
      </pre>
    </div>
  );
}

export default function EmbedHelpPage() {
  return (
    <div
      className="min-h-screen w-full px-5 py-10"
      style={{ backgroundColor: "#E8E6F5" }}
      data-testid="embed-help-page"
    >
      <div className="max-w-2xl mx-auto">
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900">
          Embed in WordPress
        </h1>
        <p className="text-slate-600 mt-2 leading-relaxed">
          Copy a snippet below and paste it into your WordPress page.
          Use the <b>Custom HTML</b> block (Block editor) or the <b>Text</b>{" "}
          tab (Classic editor).
        </p>

        <div className="mt-6 space-y-4">
          <Snippet
            label="Standard iframe embed"
            code={SNIPPETS.iframe}
            testId="copy-iframe"
          />
          <Snippet
            label="Responsive wrapper"
            code={SNIPPETS.responsive}
            testId="copy-responsive"
          />
          <Snippet
            label="iframe shortcode plugin (optional)"
            code={SNIPPETS.shortcode}
            testId="copy-shortcode"
          />
        </div>

        <div className="mt-8 bg-white rounded-2xl shadow-sm p-5">
          <h2 className="font-heading text-lg font-bold text-slate-900">
            How it works
          </h2>
          <ol className="mt-3 space-y-2 text-sm text-slate-700 list-decimal pl-5">
            <li>
              Paste the snippet on your WordPress donation page (Custom HTML
              block).
            </li>
            <li>
              When a donor clicks <b>Donate</b>, they're redirected to Stripe
              Checkout in the top window (Stripe doesn't allow embedded
              checkout).
            </li>
            <li>
              After payment, donors return to{" "}
              <code className="bg-slate-100 px-1 rounded">/success</code> with
              a confirmation message. You'll see the donation in your Stripe
              dashboard.
            </li>
            <li>
              Adjust the iframe <code>height</code> to fit your layout
              (~1100px works for most cases).
            </li>
          </ol>
        </div>

        <div className="mt-6 text-center">
          <a
            href="/"
            className="inline-block text-slate-900 font-semibold hover:underline"
          >
            ← Preview the donation widget
          </a>
        </div>
      </div>
    </div>
  );
}
